import classNames from 'class-names';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Col, ListGroup, Row } from 'react-bootstrap';

import { Icon } from '../../../Atoms/Icon';

import AddNetworkModal from './AddNetworkModal';
import styles from './advancedWifi.module.css';
import RemoveNetworkModal from './RemoveNetworkModal';

const AdvancedWifi = ({
  knownNetworks,
  onAddNetwork,
  onRemoveNetwork,
  loading = false,
  readOnly = false,
  securityModes,
}) => {
  const [loader, setLoader] = useState(loading);
  const [addNetworkModal, setAddNetworkModal] = useState(false);
  const [removeNetworkModal, setRemoveNetworkModal] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const handleSelectedNetwork = (network) => {
    setSelectedNetwork(network);
  };

  const openAddNetworkModal = () => {
    setSelectedNetwork({});
    setAddNetworkModal(true);
    setRemoveNetworkModal(false);
  };

  const openRemoveNetworkModal = () => {
    setAddNetworkModal(false);
    setRemoveNetworkModal(true);
  };

  const openSecurityModes = securityModes.reduce((result, mode) => {
    return mode.requiresPassphrase
      ? result
      : [...result, mode.name.toUpperCase()];
  }, []);

  useEffect(() => {
    setLoader(loading);
    if (!loading) {
      setAddNetworkModal(false);
      setRemoveNetworkModal(false);
    }
  }, [loading]);

  return (
    <>
      {!readOnly && (
        <Row>
          <Col className="text-right">
            <Button
              variant="light"
              disabled={readOnly}
              className={classNames(
                'border-bottom-0 border-secondary',
                styles.actionBtn,
              )}
              onClick={openAddNetworkModal}
            >
              <Icon name="add" className="text-gray-800 h5 mb-0" />
            </Button>
            <Button
              variant="light"
              disabled={!selectedNetwork.ssid || readOnly}
              onClick={openRemoveNetworkModal}
              className={classNames(
                'border-left-0 border-bottom-0 border-secondary',
                styles.actionBtn,
              )}
            >
              <Icon name="remove" className="text-gray-800 h5 mb-0" />
            </Button>
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={12}>
          <div
            style={{ padding: '0.2rem 1.5rem', fontSize: '12px' }}
            className="text-uppercase bg-gray-100 border border-bottom-0 border-secondary font-weight-bold text-gray"
          >
            Network Name
          </div>
        </Col>
        <Col md={12}>
          <ListGroup className="padded">
            {knownNetworks.map((network) => {
              const { ssid, security } = network;
              const active = selectedNetwork.ssid === ssid;
              return (
                <ListGroup.Item
                  onClick={() => handleSelectedNetwork(network)}
                  action
                  disabled={readOnly}
                  role="listitem"
                  key={ssid}
                  className={active ? 'active' : ''}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <span className="ml-2">{ssid}</span>
                    </div>
                    {!openSecurityModes.includes(security.toUpperCase()) && (
                      <Icon
                        name="lock-locked"
                        className={classNames(
                          active ? 'text-white' : 'text-gray',
                          'small',
                        )}
                      />
                    )}
                  </div>
                </ListGroup.Item>
              );
            })}
            {knownNetworks.length === 0 && (
              <ListGroup.Item role="listitem">
                <em>Network not available.</em>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Col>
      </Row>
      {addNetworkModal && (
        <AddNetworkModal
          modal={addNetworkModal}
          onHide={setAddNetworkModal}
          onAddNetwork={onAddNetwork}
          loading={loader}
          securityModes={securityModes}
        />
      )}
      {removeNetworkModal && (
        <RemoveNetworkModal
          modal={removeNetworkModal}
          onHide={setRemoveNetworkModal}
          network={selectedNetwork}
          onRemoveNetwork={onRemoveNetwork}
        />
      )}
    </>
  );
};

const networkShape = {
  ssid: PropTypes.string.isRequired,
  security: PropTypes.string.isRequired,
};

AdvancedWifi.propTypes = {
  knownNetworks: PropTypes.arrayOf(PropTypes.shape(networkShape)),
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  onAddNetwork: PropTypes.func.isRequired,
  onRemoveNetwork: PropTypes.func.isRequired,
  securityModes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      requiresPassphrase: PropTypes.bool.isRequired,
    }),
  ).isRequired,
};

export default AdvancedWifi;
